import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  format,
  parse,
  set,
  addMinutes,
  isValid,
  addDays,
  startOfWeek,
  getDay,
} from 'date-fns';
import {
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
} from '@chakra-ui/react';
import { es } from 'date-fns/locale';
import { getNextBusinessDate } from '../../utils/defaultMaintenanceService';
import ConfirmModal from '../modal/ConfirmModal';
import { normalizeDurationMinutes } from '../../utils/serviceDuration';
import { toDateOnlyYYYYMMDD } from '../../utils/dateOnly';
import { formatAvailableTimesEventTitle } from '../../utils/calendarEventTitle';
import { getAgendaViewRange } from '../../utils/agendaDateRange';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { locale: es }),
  getDay: (date) => getDay(date),
  locales: { es },
});

const WORK_START_HOUR = 7;
const WORK_START_MINUTES = 30;
const WORK_END_HOUR = 16;

/** min/max solo usan hora; el día es arbitrario (como en SchedulerCalendar). */
const CALENDAR_MIN = new Date(1970, 0, 1, WORK_START_HOUR, WORK_START_MINUTES, 0, 0);
const CALENDAR_MAX = new Date(1970, 0, 1, WORK_END_HOUR, 0, 0, 0);
const SCROLL_TO = new Date(1970, 0, 1, WORK_START_HOUR, WORK_START_MINUTES, 0, 0);

/** Por encima del modal de agenda (~1400); mismo valor en overlay y container. */
const NESTED_DETAIL_MODAL_Z = 2000;

const formatTimeValue = (date) => format(date, 'HH:mm:ss');
const formatDateValue = (date) => format(date, 'yyyy-MM-dd');

const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const normalized =
    typeof dateStr === 'string'
      ? toDateOnlyYYYYMMDD(dateStr) || dateStr.slice(0, 10)
      : toDateOnlyYYYYMMDD(dateStr) || dateStr;
  const parsed = parse(normalized, 'yyyy-MM-dd', new Date());
  return isValid(parsed) ? parsed : null;
};

const isWeekend = (date) => {
  const day = getDay(date);
  return day === 0 || day === 6;
};

const parseSlotDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const datePart =
    typeof dateStr === 'string'
      ? toDateOnlyYYYYMMDD(dateStr) || dateStr.slice(0, 10)
      : toDateOnlyYYYYMMDD(dateStr) || format(new Date(dateStr), 'yyyy-MM-dd');
  const normalizedTime = String(timeStr).length === 5 ? `${timeStr}:00` : String(timeStr);
  const parsed = parse(
    `${datePart} ${normalizedTime}`,
    'yyyy-MM-dd HH:mm:ss',
    new Date(),
  );
  return isValid(parsed) ? parsed : null;
};

const normalizeSlotMoment = (date) =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    0,
    0,
  );

const AvailableTimes = ({
  selectedDateString,
  selectedTimeString,
  duration,
  serviceEvents = [],
  searchByDate,
  show,
  appointmentList,
  handleServiceChange,
  holidayList,
  selectedMechanicId,
  disableAutoSelect = false,
  autoRecommend = true,
  startOnCurrentWeek = false,
  recommendTick = 0,
  manualScheduleTick = 0,
  calendarHeight = '70vh',
  excludeAppointmentId = null,
  onBlockingOverlayChange,
  onAppointmentRangeChange,
  allowOccupiedSlot = false,
}) => {
  const durationMinutes = normalizeDurationMinutes(duration, serviceEvents);
  const durationRef = useRef(durationMinutes);
  durationRef.current = durationMinutes;

  const [recommendedEvent, setRecommendedEvent] = useState(null);
  const [userSelectedEvent, setUserSelectedEvent] = useState(null);
  const [occupiedEvents, setOccupiedEvents] = useState([]);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState('work_week');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isMechanicConflictModalOpen, setIsMechanicConflictModalOpen] =
    useState(false);
  const [pendingSlot, setPendingSlot] = useState(null);
  const [conflictingMechanicAppointment, setConflictingMechanicAppointment] =
    useState(null);
  /** 'select' = al elegir slot | 'operatorChange' = al cambiar operario */
  const [mechanicConflictMode, setMechanicConflictMode] = useState('select');
  const prevMechanicIdRef = useRef(selectedMechanicId);


  const userPickedSlotRef = useRef(false);
  const pickedStartRef = useRef(null);
  const blockAutoRecommendRef = useRef(false);
  const handleServiceChangeRef = useRef(handleServiceChange);
  handleServiceChangeRef.current = handleServiceChange;

  const toast = useToast();

  useEffect(() => {
    const blocking =
      isMechanicConflictModalOpen || isConfirmModalOpen || isModalOpen;
    onBlockingOverlayChange?.(blocking);
    return () => onBlockingOverlayChange?.(false);
  }, [
    isMechanicConflictModalOpen,
    isConfirmModalOpen,
    isModalOpen,
    onBlockingOverlayChange,
  ]);

  // Reiniciar solo al cambiar modo de búsqueda (NO al elegir fecha/hora ni operario)
  const resetSelectionKey = String(searchByDate);

  useEffect(() => {
    userPickedSlotRef.current = false;
    pickedStartRef.current = null;
    blockAutoRecommendRef.current = false;
    setUserSelectedEvent(null);
  }, [resetSelectionKey]);

  const applySelection = useCallback((start) => {
    const slotStart = normalizeSlotMoment(start);
    const mins = durationRef.current;
    const slotEnd = addMinutes(slotStart, mins);
    pickedStartRef.current = slotStart;
    userPickedSlotRef.current = true;
    blockAutoRecommendRef.current = false;
    setRecommendedEvent(null);
    setUserSelectedEvent({
      title: `Seleccionado (${mins} min)`,
      status: 'new',
      start: slotStart,
      end: slotEnd,
    });
    setCalendarDate(slotStart);
    handleServiceChangeRef.current('time', formatTimeValue(slotStart));
    handleServiceChangeRef.current('date', formatDateValue(slotStart));
  }, []);

  useEffect(() => {
    if (!manualScheduleTick) return;
    const start = parseSlotDateTime(selectedDateString, selectedTimeString);
    if (start) applySelection(start);
  }, [manualScheduleTick, selectedDateString, selectedTimeString, applySelection]);

  // Al cambiar duración del servicio, extender el bloque sin borrar el inicio elegido
  useEffect(() => {
    if (!pickedStartRef.current || !userPickedSlotRef.current) return;
    const slotStart = pickedStartRef.current;
    const slotEnd = addMinutes(slotStart, durationMinutes);
    setUserSelectedEvent({
      title: `Seleccionado (${durationMinutes} min)`,
      status: 'new',
      start: slotStart,
      end: slotEnd,
    });
  }, [durationMinutes]);

  const parsedSelectedDate = useMemo(() => {
    if (!selectedDateString) return null;
    return parseLocalDate(selectedDateString);
  }, [selectedDateString]);

  const onRangeChangeRef = useRef(onAppointmentRangeChange);
  onRangeChangeRef.current = onAppointmentRangeChange;
  const lastRequestedRangeRef = useRef('');

  useEffect(() => {
    const notify = onRangeChangeRef.current;
    if (!notify) return;
    const anchor = calendarDate || parsedSelectedDate || new Date();
    const { from, to } = getAgendaViewRange(anchor, calendarView);
    const rangeKey = `${from}|${to}`;
    if (lastRequestedRangeRef.current === rangeKey) return;
    lastRequestedRangeRef.current = rangeKey;
    notify(from, to);
  }, [calendarDate, calendarView, parsedSelectedDate]);

  const generateOccupiedEvents = useCallback(
    (appointments) => {
      const occupied = (appointments || [])
        .filter((apt) => {
          if (apt?.status === 'Cancelado') return false;
          if (
            excludeAppointmentId &&
            Number(apt.id) === Number(excludeAppointmentId)
          ) {
            return false;
          }
          return true;
        })
        .map((appointment) => {
            const {
              date,
              time,
              estimatedTime,
              status,
              mechanic,
              service,
              id,
              mechanicId,
            } = appointment;
            const start = parseSlotDateTime(date, time);
            if (!start) return null;
            const end = addMinutes(
              start,
              normalizeDurationMinutes(estimatedTime, service?.servicesEvents),
            );
            return {
              id,
              mechanicId,
              title: formatAvailableTimesEventTitle(appointment),
              status,
              start,
              end,
            };
          },
        );

      const holidayEvents = (holidayList || [])
        .map(({ date, name }) => {
          const day = parseLocalDate(date);
          if (!day) return null;
          const dayStart = set(day, {
            hours: 0,
            minutes: 0,
            seconds: 0,
            milliseconds: 0,
          });
          return {
            title: name,
            status: 'holiday',
            allDay: true,
            start: dayStart,
            end: addDays(dayStart, 1),
          };
        })
        .filter(Boolean);

      return [...occupied.filter(Boolean), ...holidayEvents];
    },
    [holidayList, excludeAppointmentId],
  );

  const hasConflict = useCallback(
    (start, end, eventsList) => {
      return eventsList.some((event) => {
        if (event.status === 'holiday' || event.allDay) {
          return formatDateValue(start) === formatDateValue(event.start);
        }

        if (selectedMechanicId) {
          const originalAppointment = (appointmentList || []).find((apt) => {
            if (apt?.status === 'Cancelado') return false;
            const aptStart = parseSlotDateTime(apt.date, apt.time);
            if (!aptStart) return false;
            const aptEnd = addMinutes(
              aptStart,
              normalizeDurationMinutes(
                apt.estimatedTime,
                apt.service?.servicesEvents,
              ),
            );
            return (
              aptStart.getTime() === event.start.getTime() &&
              aptEnd.getTime() === event.end.getTime()
            );
          });

          if (
            originalAppointment &&
            Number(originalAppointment.mechanicId) !==
              Number(selectedMechanicId)
          ) {
            return false;
          }
        }

        return (
          (start >= event.start && start < event.end) ||
          (end > event.start && end <= event.end) ||
          (start <= event.start && end >= event.end)
        );
      });
    },
    [appointmentList, selectedMechanicId],
  );

  const findRecommendedSlot = useCallback(() => {
    if (!durationMinutes || durationMinutes <= 0) return null;

    const totalWorkMinutes =
      (WORK_END_HOUR - WORK_START_HOUR) * 60 - WORK_START_MINUTES;
    if (durationMinutes > totalWorkMinutes) return null;

    const baseEvents = generateOccupiedEvents(appointmentList);
    let currentDate = getNextBusinessDate();
    if (!isValid(currentDate)) return null;

    let safety = 0;
    while (safety < 60) {
      safety += 1;
      if (isWeekend(currentDate)) {
        currentDate = addDays(currentDate, 1);
        continue;
      }

      let recommendedStart = set(currentDate, {
        hours: WORK_START_HOUR,
        minutes: WORK_START_MINUTES,
        seconds: 0,
        milliseconds: 0,
      });
      const dayEnd = set(currentDate, {
        hours: WORK_END_HOUR,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      });
      let recommendedEnd = addMinutes(recommendedStart, durationMinutes);

      while (recommendedEnd <= dayEnd) {
        if (!hasConflict(recommendedStart, recommendedEnd, baseEvents)) {
          return { start: recommendedStart, end: recommendedEnd };
        }
        recommendedStart = addMinutes(recommendedStart, 30);
        recommendedEnd = addMinutes(recommendedStart, durationMinutes);
      }
      currentDate = addDays(currentDate, 1);
    }
    return null;
  }, [
    durationMinutes,
    appointmentList,
    generateOccupiedEvents,
    hasConflict,
  ]);

  const applyRecommendedSchedule = useCallback(() => {
    userPickedSlotRef.current = false;
    pickedStartRef.current = null;
    setUserSelectedEvent(null);
    // Limpiar fecha/hora para permitir recalcular el recomendado
    handleServiceChangeRef.current('time', '');
    handleServiceChangeRef.current('date', '');

    const slot = findRecommendedSlot();
    if (!slot) {
      setRecommendedEvent(null);
      toast({
        title: 'Sin horario disponible',
        description: 'No se encontró un horario recomendado libre.',
        status: 'warning',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    setRecommendedEvent({
      title: 'Horario Recomendado',
      start: slot.start,
      end: slot.end,
      status: 'recommended',
    });
    setCalendarDate(slot.start);
    handleServiceChangeRef.current('time', formatTimeValue(slot.start));
    handleServiceChangeRef.current('date', formatDateValue(slot.start));
    toast({
      title: 'Horario recomendado',
      description: `${format(slot.start, 'dd/MM/yyyy HH:mm')} – ${format(slot.end, 'HH:mm')}`,
      status: 'info',
      duration: 2500,
      isClosable: true,
    });
  }, [findRecommendedSlot, toast]);

  const appliedRecommendTickRef = useRef(0);
  useEffect(() => {
    if (!recommendTick || recommendTick === appliedRecommendTickRef.current) {
      return;
    }
    appliedRecommendTickRef.current = recommendTick;
    applyRecommendedSchedule();
  }, [recommendTick, applyRecommendedSchedule]);

  useEffect(() => {
    setOccupiedEvents(generateOccupiedEvents(appointmentList));
  }, [appointmentList, generateOccupiedEvents]);

  useEffect(() => {
    if (blockAutoRecommendRef.current) return;
    if (parsedSelectedDate && !userPickedSlotRef.current) {
      setCalendarDate(parsedSelectedDate);
    } else if (!searchByDate && !userPickedSlotRef.current && !parsedSelectedDate) {
      setCalendarDate(startOnCurrentWeek ? new Date() : getNextBusinessDate());
    }
  }, [searchByDate, parsedSelectedDate, startOnCurrentWeek]);

  useEffect(() => {
    if (!autoRecommend) return;

    if (!durationMinutes || durationMinutes <= 0) {
      setRecommendedEvent(null);
      return;
    }

    if (searchByDate) {
      setRecommendedEvent(null);
      return;
    }

    // No recalcular si el usuario ya eligió horario, o si ya hay fecha+hora asignadas
    // (evita que al cambiar operario se pise el turno y desaparezca la selección)
    if (userPickedSlotRef.current) return;
    if (blockAutoRecommendRef.current) return;
    if (selectedDateString && selectedTimeString) return;

    const slot = findRecommendedSlot();
    if (!slot) {
      setRecommendedEvent(null);
      return;
    }

    setRecommendedEvent({
      title: 'Horario Recomendado',
      start: slot.start,
      end: slot.end,
      status: 'recommended',
    });

    setCalendarDate(slot.start);

    if (!disableAutoSelect) {
      handleServiceChangeRef.current('time', formatTimeValue(slot.start));
      handleServiceChangeRef.current('date', formatDateValue(slot.start));
    }
  }, [
    autoRecommend,
    durationMinutes,
    searchByDate,
    selectedMechanicId,
    selectedDateString,
    selectedTimeString,
    appointmentList,
    disableAutoSelect,
    findRecommendedSlot,
    resetSelectionKey,
  ]);

  const allEvents = useMemo(() => {
    const list = [...occupiedEvents];
    if (recommendedEvent && !userSelectedEvent) {
      list.push(recommendedEvent);
    } else if (recommendedEvent) {
      list.push({ ...recommendedEvent, title: 'Horario Recomendado' });
    }
    if (userSelectedEvent) {
      list.push(userSelectedEvent);
    }
    return list;
  }, [occupiedEvents, recommendedEvent, userSelectedEvent]);

  const checkMechanicConflict = useCallback(
    (start, end, mechanicId = selectedMechanicId) => {
      if (!mechanicId) return null;
      const slotStart = normalizeSlotMoment(start);
      const slotEnd = normalizeSlotMoment(end);

      return (appointmentList || []).find((appointment) => {
        const statusName = String(appointment?.status || '').toLowerCase();
        if (statusName === 'cancelado') return false;
        if (
          excludeAppointmentId &&
          Number(appointment.id) === Number(excludeAppointmentId)
        ) {
          return false;
        }
        const aptMechanicId =
          appointment.mechanicId ?? appointment.mechanic?.id;
        if (Number(aptMechanicId) !== Number(mechanicId)) return false;

        const appointmentStart = parseSlotDateTime(
          appointment.date,
          appointment.time,
        );
        if (!appointmentStart) return false;
        const appointmentEnd = addMinutes(
          appointmentStart,
          normalizeDurationMinutes(
            appointment.estimatedTime,
            appointment.service?.servicesEvents,
          ),
        );
        return slotStart < appointmentEnd && slotEnd > appointmentStart;
      });
    },
    [appointmentList, selectedMechanicId, excludeAppointmentId],
  );

  const clearSelectedSchedule = useCallback(() => {
    const keepView =
      pickedStartRef.current ||
      (selectedDateString
        ? parseSlotDateTime(selectedDateString, selectedTimeString || '12:00:00')
        : null);

    userPickedSlotRef.current = false;
    pickedStartRef.current = null;
    blockAutoRecommendRef.current = true;
    setUserSelectedEvent(null);
    setRecommendedEvent(null);
    // Solo limpia la hora; mantiene la fecha para no sacar del día/semana actual
    handleServiceChangeRef.current('time', '');
    if (keepView) {
      setCalendarDate(keepView);
      handleServiceChangeRef.current('date', formatDateValue(keepView));
    }
  }, [selectedDateString, selectedTimeString]);

  // Si cambia el operario y el horario elegido ya está ocupado, avisar
  useEffect(() => {
    const prev = prevMechanicIdRef.current;
    if (String(prev ?? '') === String(selectedMechanicId ?? '')) {
      return;
    }
    prevMechanicIdRef.current = selectedMechanicId;

    if (!selectedMechanicId) return;
    if (allowOccupiedSlot) return;
    if (prev == null || prev === '') return;

    const start =
      pickedStartRef.current ||
      parseSlotDateTime(selectedDateString, selectedTimeString);
    if (!start) return;

    const mins = durationRef.current || durationMinutes || 30;
    const end = addMinutes(start, mins);
    const conflict = checkMechanicConflict(start, end, selectedMechanicId);
    if (!conflict) return;

    // Mantener visible el bloque amarillo mientras se decide
    pickedStartRef.current = start;
    userPickedSlotRef.current = true;
    setRecommendedEvent(null);
    setUserSelectedEvent({
      title: `Seleccionado (${mins} min)`,
      status: 'new',
      start,
      end,
    });
    setCalendarDate(start);

    setPendingSlot({ start, end });
    setConflictingMechanicAppointment(conflict);
    setMechanicConflictMode('operatorChange');
    // Diferir el open: el click del <select> puede cerrar el modal al instante
    const timer = window.setTimeout(() => {
      setIsMechanicConflictModalOpen(true);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [
    selectedMechanicId,
    selectedDateString,
    selectedTimeString,
    checkMechanicConflict,
    durationMinutes,
    allowOccupiedSlot,
  ]);

  const validateEventTime = (end) => end.getHours() <= WORK_END_HOUR;

  const confirmSchedule = useCallback(
    (start) => {
      applySelection(normalizeSlotMoment(start));
    },
    [applySelection],
  );

  const handleConfirmSlot = () => {
    if (pendingSlot) confirmSchedule(pendingSlot.start, pendingSlot.end);
    setIsConfirmModalOpen(false);
    setPendingSlot(null);
  };

  const handleCancelConfirm = () => {
    setIsConfirmModalOpen(false);
    setPendingSlot(null);
  };

  const handleConfirmMechanicConflict = () => {
    if (mechanicConflictMode === 'operatorChange') {
      // Mantener el horario seleccionado pese al conflicto
      setIsMechanicConflictModalOpen(false);
      setPendingSlot(null);
      setConflictingMechanicAppointment(null);
      setMechanicConflictMode('select');
      return;
    }
    if (!pendingSlot) return;
    const end = addMinutes(pendingSlot.start, durationRef.current);
    if (!validateEventTime(end)) {
      setIsMechanicConflictModalOpen(false);
      setIsConfirmModalOpen(true);
      return;
    }
    confirmSchedule(pendingSlot.start, end);
    setIsMechanicConflictModalOpen(false);
    setPendingSlot(null);
    setConflictingMechanicAppointment(null);
  };

  const handleCancelMechanicConflict = () => {
    if (mechanicConflictMode === 'operatorChange') {
      setIsMechanicConflictModalOpen(false);
      setPendingSlot(null);
      setConflictingMechanicAppointment(null);
      setMechanicConflictMode('select');
      clearSelectedSchedule();
      toast({
        title: 'Selección cancelada',
        description: 'Elija otro horario en el calendario.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setIsMechanicConflictModalOpen(false);
    setPendingSlot(null);
    setConflictingMechanicAppointment(null);
  };

  const handleSlotSelect = (slotInfo) => {
    const { start, action } = slotInfo;
    if (action === 'select') return;

    const slotStart = normalizeSlotMoment(start);
    const slotDate = formatDateValue(slotStart);
    const isHoliday = holidayList.some((holiday) => {
      const holidayDate = parseLocalDate(
        typeof holiday.date === 'string'
          ? holiday.date.slice(0, 10)
          : holiday.date,
      );
      return holidayDate && formatDateValue(holidayDate) === slotDate;
    });

    if (isHoliday || isWeekend(slotStart)) {
      toast({
        title: 'No disponible',
        description: 'Feriado o fin de semana.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const end = addMinutes(slotStart, durationRef.current);
    const conflictingAppointment = allowOccupiedSlot
      ? null
      : checkMechanicConflict(slotStart, end);

    if (conflictingAppointment) {
      setPendingSlot({ start: slotStart, end });
      setConflictingMechanicAppointment(conflictingAppointment);
      setMechanicConflictMode('select');
      setIsMechanicConflictModalOpen(true);
      return;
    }

    if (!validateEventTime(end)) {
      setPendingSlot({ start: slotStart, end });
      setIsConfirmModalOpen(true);
      return;
    }

    confirmSchedule(slotStart, end);
  };

  const handleEventClick = (event) => {
    if (event.status === 'recommended') {
      confirmSchedule(event.start, event.end);
      return;
    }
    if (event.status === 'new') return;
    if (event.status === 'holiday') return;

    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const eventStyleGetter = (event) => {
    if (event.status === 'recommended') {
      return {
        style: {
          backgroundColor: 'lightgreen',
          borderRadius: '4px',
          opacity: 0.85,
          color: '#1a472a',
          border: '1px dashed #2e7d32',
          pointerEvents: 'auto',
        },
      };
    }
    if (event.status === 'new') {
      return {
        style: {
          backgroundColor: '#fff300',
          borderRadius: '4px',
          opacity: 1,
          color: '#000',
          border: '2px solid #e6a800',
          zIndex: 10,
          fontWeight: 'bold',
        },
      };
    }
    if (event.status === 'holiday') {
      return {
        style: {
          backgroundColor: '#fed7d7',
          borderRadius: '4px',
          opacity: 0.9,
          color: '#742a2a',
          border: '1px solid #fc8181',
          pointerEvents: 'none',
        },
      };
    }
    return {
      style: {
        backgroundColor: 'lightcoral',
        borderRadius: '4px',
        opacity: 0.9,
        color: '#fff',
        border: '1px solid #e57373',
        pointerEvents: 'auto',
      },
    };
  };

  const CustomToolbar = ({ label, onNavigate, onView }) => {
    const year = calendarDate.getFullYear();
    return (
      <div className="rbc-toolbar">
        <span className="rbc-btn-group">
          <button type="button" onClick={() => onNavigate('TODAY')}>
            Hoy
          </button>
          <button type="button" onClick={() => onNavigate('PREV')}>
            Anterior
          </button>
          <button type="button" onClick={() => onNavigate('NEXT')}>
            Siguiente
          </button>
        </span>
        <span className="rbc-toolbar-label">
          {`${label.charAt(0).toUpperCase()}${label.slice(1)} / ${year}`}
        </span>
        <select
          onChange={(e) => {
            const v = e.target.value;
            setCalendarView(v);
            onView(v);
          }}
          value={calendarView}
          style={{
            padding: '6px 10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '12px',
          }}
        >
          <option value="work_week">Semana</option>
          <option value="day">Día</option>
        </select>
      </div>
    );
  };

  if (!show) return null;

  if (searchByDate && !parsedSelectedDate) {
    return (
      <div
        style={{
          height: calendarHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#718096',
          fontSize: '14px',
          textAlign: 'center',
          padding: '0 16px',
        }}
      >
        Seleccione una fecha en &quot;Fecha desde&quot; para ver los horarios
        disponibles
      </div>
    );
  }

  return (
    <div style={{ height: calendarHeight, minHeight: 0, overflow: 'hidden' }}>
      <Calendar
        localizer={localizer}
        culture="es"
        events={allEvents}
        startAccessor="start"
        endAccessor="end"
        selectable="ignoreEvents"
        view={calendarView}
        views={{ work_week: true, day: true }}
        defaultView="work_week"
        step={30}
        timeslots={1}
        min={CALENDAR_MIN}
        max={CALENDAR_MAX}
        scrollToTime={SCROLL_TO}
        date={calendarDate}
        onNavigate={setCalendarDate}
        onView={setCalendarView}
        style={{ height: '100%' }}
        onSelectSlot={handleSlotSelect}
        onSelectEvent={handleEventClick}
        longPressThreshold={10}
        eventPropGetter={eventStyleGetter}
        components={{ toolbar: CustomToolbar }}
        dayLayoutAlgorithm="no-overlap"
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        blockScrollOnMount={false}
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay zIndex={NESTED_DETAIL_MODAL_Z} />
        <ModalContent
          zIndex={NESTED_DETAIL_MODAL_Z}
          containerProps={{
            zIndex: NESTED_DETAIL_MODAL_Z,
            pointerEvents: 'auto',
          }}
        >
          <ModalHeader>Detalles del turno</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedEvent && (
              <>
                <p>
                  <strong>Título:</strong> {selectedEvent.title}
                </p>
                <p>
                  <strong>Inicio:</strong>{' '}
                  {format(selectedEvent.start, 'dd/MM/yyyy HH:mm')}
                </p>
                <p>
                  <strong>Fin:</strong>{' '}
                  {format(selectedEvent.end, 'dd/MM/yyyy HH:mm')}
                </p>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="adminPrimary" onClick={() => setIsModalOpen(false)}>
              Cerrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={handleCancelConfirm}
        onConfirm={handleConfirmSlot}
        header="Horario fuera del rango"
        message="El horario excede el fin de jornada (16:00). ¿Desea agendar igualmente?"
        nested={Boolean(onBlockingOverlayChange)}
      />
      <ConfirmModal
        isOpen={isMechanicConflictModalOpen}
        onClose={handleCancelMechanicConflict}
        onConfirm={handleConfirmMechanicConflict}
        header="Conflicto de operario"
        nested
        zIndex={2500}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        confirmLabel={
          mechanicConflictMode === 'operatorChange'
            ? 'Mantener horario'
            : 'Agendar igualmente'
        }
        cancelLabel={
          mechanicConflictMode === 'operatorChange'
            ? 'Elegir otro horario'
            : 'Cancelar'
        }
        message={
          conflictingMechanicAppointment
            ? (() => {
                const appointmentStart = parseSlotDateTime(
                  conflictingMechanicAppointment.date,
                  conflictingMechanicAppointment.time,
                );
                if (!appointmentStart) {
                  return mechanicConflictMode === 'operatorChange'
                    ? 'Este operario ya tiene un servicio en ese horario. ¿Desea mantener la selección o elegir otro horario?'
                    : 'El operario tiene otro turno en ese horario. ¿Continuar?';
                }
                const appointmentEnd = addMinutes(
                  appointmentStart,
                  normalizeDurationMinutes(
                    conflictingMechanicAppointment.estimatedTime,
                    conflictingMechanicAppointment.service?.servicesEvents,
                  ),
                );
                if (mechanicConflictMode === 'operatorChange') {
                  return `Este operario ya tiene turno de ${format(appointmentStart, 'HH:mm')} a ${format(appointmentEnd, 'HH:mm')}. ¿Desea mantener el horario seleccionado o elegir otro?`;
                }
                return `El operario ya tiene turno de ${format(appointmentStart, 'HH:mm')} a ${format(appointmentEnd, 'HH:mm')}. ¿Agendar igualmente?`;
              })()
            : mechanicConflictMode === 'operatorChange'
              ? 'Este operario ya tiene un servicio en ese horario. ¿Desea mantener la selección o elegir otro horario?'
              : 'El operario tiene otro turno en ese horario. ¿Continuar?'
        }
      />
    </div>
  );
};

export default AvailableTimes;
